<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcContractProtocolHeader',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'deleted']);

  const props = defineProps({
    protocol: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const messages = ref({
    handoverTypes: {
      moving_in: $fn.value.pgettext('HandoverType', 'Moving-in protocol'),
      moving_out: $fn.value.pgettext('HandoverType', 'Moving-out protocol'),
    },
    newTransaction: $fn.value.pgettext('ContractProtocol', 'New protocol'),
    editTransaction: $fn.value.pgettext('ContractProtocol', 'Edit protocol'),
  });
  const ux = ref([]);

  const $data = computed(() => ({
    messages: messages.value,
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'messages' }, { name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { messages, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const isNew = computed(() => {
    return props.protocol.isNew;
  });
  const resourceTitle = computed(() => {
    if (loaded.value) {
      return messages.value.handoverTypes[props.protocol.handover_type];
    }
    return null;
  });
  const resourceKey = computed(() => {
    if (loaded.value) {
      return isNew.value
        ? messages.value.newTransaction
        : messages.value.editTransaction;
    }
    return null;
  });

  watch(
    () => props.protocol,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('protocol');
    }
  );

  const act_1040167445267876_emit_3sq0vm1ow_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_4942415580156499_emit_78rkt1oif_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_4942415580156499_request_yhxavflnm_getActionArgs = async (
    $event
  ) => {
    return {
      url: props.protocol.links.self,
      method: 'patch',
      data: {
        etag: props.protocol.etag,
        locked: true,
      },
    };
  };
  const act_49312645155713_emit_lumqm4a60_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_49312645155713_request_d0cu39wwb_getActionArgs = async ($event) => {
    return {
      url: props.protocol.links.self,
      method: 'patch',
      data: {
        etag: props.protocol.etag,
        locked: false,
      },
    };
  };
  const act_237785848579802_emit_yiiq12921_getActionArgs = async ($event) => {
    return {
      event: 'deleted',
    };
  };

  const act_1040167445267876_emit_3sq0vm1ow = new actionTypes.EmitAction({
    actionArgs: act_1040167445267876_emit_3sq0vm1ow_getActionArgs,
    displayName: 'reload',
  });
  const inst_evh_1040167445267876_clickPreventStop_w3u5uo7go =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_emit_3sq0vm1ow,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_1040167445267876_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1040167445267876_clickPreventStop_w3u5uo7go],
      event: 'click.prevent.stop',
    });
  const act_4942415580156499_emit_78rkt1oif = new actionTypes.EmitAction({
    actionArgs: act_4942415580156499_emit_78rkt1oif_getActionArgs,
  });
  const inst_evh_4942415580156499_success_yo8f9up92 =
    new actionTypes.EventHandlerImpl({
      action: act_4942415580156499_emit_78rkt1oif,
      event: 'success',
      displayName: 'emit',
    });
  const act_4942415580156499_request_yhxavflnm = new actionTypes.RequestAction({
    actionArgs: act_4942415580156499_request_yhxavflnm_getActionArgs,
    displayName: 'lock',
    events: [inst_evh_4942415580156499_success_yo8f9up92],
  });
  const inst_evh_4942415580156499_clickPreventStop_h9u0uub3b =
    new actionTypes.EventHandlerImpl({
      action: act_4942415580156499_request_yhxavflnm,
      event: 'click.prevent.stop',
      displayName: 'lock',
    });
  const inst_evh_4942415580156499_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4942415580156499_clickPreventStop_h9u0uub3b],
      event: 'click.prevent.stop',
    });
  const act_49312645155713_emit_lumqm4a60 = new actionTypes.EmitAction({
    actionArgs: act_49312645155713_emit_lumqm4a60_getActionArgs,
  });
  const inst_evh_49312645155713_success_3sxl1v3yr =
    new actionTypes.EventHandlerImpl({
      action: act_49312645155713_emit_lumqm4a60,
      event: 'success',
      displayName: 'emit',
    });
  const act_49312645155713_request_d0cu39wwb = new actionTypes.RequestAction({
    actionArgs: act_49312645155713_request_d0cu39wwb_getActionArgs,
    displayName: 'lock',
    events: [inst_evh_49312645155713_success_3sxl1v3yr],
  });
  const inst_evh_49312645155713_clickPreventStop_ssq10jmz7 =
    new actionTypes.EventHandlerImpl({
      action: act_49312645155713_request_d0cu39wwb,
      event: 'click.prevent.stop',
      displayName: 'lock',
    });
  const inst_evh_49312645155713_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_49312645155713_clickPreventStop_ssq10jmz7],
      event: 'click.prevent.stop',
    });
  const act_237785848579802_emit_yiiq12921 = new actionTypes.EmitAction({
    actionArgs: act_237785848579802_emit_yiiq12921_getActionArgs,
  });
  const inst_evh_237785848579802_success_fdf04oxqv =
    new actionTypes.EventHandlerImpl({
      action: act_237785848579802_emit_yiiq12921,
      event: 'success',
      displayName: 'emit',
    });
  const inst_evh_237785848579802_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_237785848579802_success_fdf04oxqv],
    event: 'success',
  });

  const actions = {
    inst_evh_1040167445267876_clickPreventStop_w3u5uo7go,
    reload: inst_evh_1040167445267876_clickPreventStop_w3u5uo7go,
    inst_evh_1040167445267876_clickPreventStop,
    inst_evh_4942415580156499_success_yo8f9up92,
    inst_evh_4942415580156499_clickPreventStop_h9u0uub3b,
    lock: inst_evh_4942415580156499_clickPreventStop_h9u0uub3b,
    inst_evh_4942415580156499_clickPreventStop,
    inst_evh_49312645155713_success_3sxl1v3yr,
    inst_evh_49312645155713_clickPreventStop_ssq10jmz7,
    inst_evh_49312645155713_clickPreventStop,
    inst_evh_237785848579802_success_fdf04oxqv,
    inst_evh_237785848579802_success,
  };

  const evh_1040167445267876_clickPreventStop_w3u5uo7go = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1040167445267876_clickPreventStop_w3u5uo7go.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4942415580156499_success_yo8f9up92 = async (event, scope) => {
    return await actions.inst_evh_4942415580156499_success_yo8f9up92.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4942415580156499_clickPreventStop_h9u0uub3b = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4942415580156499_clickPreventStop_h9u0uub3b.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4942415580156499_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4942415580156499_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_49312645155713_success_3sxl1v3yr = async (event, scope) => {
    return await actions.inst_evh_49312645155713_success_3sxl1v3yr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_49312645155713_clickPreventStop_ssq10jmz7 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_49312645155713_clickPreventStop_ssq10jmz7.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_49312645155713_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_49312645155713_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_237785848579802_success_fdf04oxqv = async (event, scope) => {
    return await actions.inst_evh_237785848579802_success_fdf04oxqv.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_237785848579802_success = async (event, scope) => {
    return await actions.inst_evh_237785848579802_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox alignItems="center" class="items-spacing w-100" v-if="loaded">
    <ItpBox direction="column">
      <ItpBox>
        <ItpText muted class="mb-0">
          {{ resourceTitle }}
        </ItpText>
      </ItpBox>
      <ItpText tag="h4" :text="resourceKey" class="mb-0"> </ItpText>
    </ItpBox>
    <div class="btn-toolbar ms-auto flex-nowrap gap-2">
      <ItpButton
        icon="fa-sync-alt"
        :tooltip="gettext('Reload')"
        :spinning="actions.reload.isRunning"
        variant="light"
        v-if="!isNew"
        @click.prevent.stop="evh_1040167445267876_clickPreventStop($event, {})"
      >
      </ItpButton>
      <ItpButton
        variant="light"
        icon="icon-park-outline:preview-open"
        :href="protocol.preview_url"
        :tooltip="pgettext('ContractProtocol', 'Open preview')"
        target="_blank"
        use-auth
        show-spinner
        v-if="protocol.preview_url"
      >
      </ItpButton>
      <BDropdown right variant="light" class="border" v-if="!isNew">
        <template #button-content>
          <ItpIcon fa="bars" class="mx-1"> </ItpIcon>
        </template>
        <ItpButton
          dropdown-item
          :text="pgettext('BtnLockText', 'Lock')"
          :tooltip="pgettext('BtnLockTooltip', 'Lock')"
          variant="default"
          v-if="!protocol.isNew && protocol.meta.can_lock"
          @click.prevent.stop="
            evh_4942415580156499_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          dropdown-item
          :text="pgettext('BtnLockText', 'Unlock')"
          :tooltip="pgettext('BtnLockTooltip', 'Unlock')"
          variant="default"
          v-if="!protocol.isNew && protocol.meta.can_unlock"
          @click.prevent.stop="evh_49312645155713_clickPreventStop($event, {})"
        >
        </ItpButton>
        <BtnDeleteResource
          no-icon
          dropdown-item
          :resource="protocol"
          :tooltip="pgettext('Payment', 'Delete protocol')"
          :disabled="protocol.isNew || protocol.protocol_status != 'draft'"
          v-if="!isNew"
          @success="evh_237785848579802_success($event, {})"
        >
        </BtnDeleteResource>
      </BDropdown>
    </div>
  </ItpBox>
</template>
